import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Check, X, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  fetchApplications,
  updateApplication,
  deleteApplication,
  createCreator,
} from "@/lib/api";
import { formatFollowers } from "@/lib/format";

const statusColor = {
  Applied: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  Reviewing: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  Accepted: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  Rejected: "bg-red-500/10 text-red-300 border-red-500/30",
};

const Applications = () => {
  const [apps, setApps] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => fetchApplications().then(setApps).catch(() => setApps([]));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (a, status) => {
    setBusyId(a.id);
    try {
      await updateApplication(a.id, { status });
      setApps((cur) => cur.map((x) => (x.id === a.id ? { ...x, status } : x)));
      toast.success(`Marked ${status}`);
      if (status === "Accepted") {
        // Auto-create a creator entry
        try {
          await createCreator({
            name: a.name,
            handle: a.handle,
            location: a.location || "—",
            primaryPlatform: a.primaryPlatform,
            followers: a.followers || 0,
            er: 0,
            rating: 4,
            niches: (a.niches || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
            languages: (a.languages || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
            platforms: [{ name: a.primaryPlatform, followers: a.followers || 0 }],
            rateCard: { reel: 0, post: 0, story: 0 },
            audience: { female: 50, male: 50, ageGroups: [] },
            avgViews: 0,
            notes: a.about || "",
            history: [],
            avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(a.name)}`,
          });
          toast.success(`${a.name} added to creator roster`);
        } catch (e) {
          // Likely a duplicate handle — surface for debugging but don't toast
          console.warn("Auto-create creator from application failed", e);
        }
      }
    } catch (_e) {
      toast.error("Could not update");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (a) => {
    setBusyId(a.id);
    try {
      await deleteApplication(a.id);
      setApps((cur) => cur.filter((x) => x.id !== a.id));
      toast.success("Removed");
    } catch (_e) {
      toast.error("Could not remove");
    } finally {
      setBusyId(null);
    }
  };

  if (apps === null) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="applications-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Creator Applications</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {apps.length} application{apps.length === 1 ? "" : "s"} ·{" "}
            {apps.filter((a) => a.status === "Applied").length} new
          </p>
        </div>
      </div>

      {apps.length === 0 ? (
        <div
          className="rounded-xl border border-white/8 bg-white/[0.02] p-12 text-center"
          data-testid="applications-empty"
        >
          <p className="text-zinc-400">No applications yet — share the landing page.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="applications-grid">
          {apps.map((a) => (
            <article
              key={a.id}
              className="rounded-xl border border-white/8 bg-white/[0.02] p-5 hover:border-blue-500/30 transition-colors"
              data-testid={`application-${a.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-base font-semibold text-zinc-50">{a.name}</div>
                  <div className="text-xs text-zinc-500">
                    {a.handle} · {a.location || "—"}
                  </div>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
                    statusColor[a.status] || "bg-zinc-500/10 text-zinc-300 border-zinc-700"
                  }`}
                >
                  {a.status}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                <div className="rounded-lg bg-white/[0.02] border border-white/5 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Platform</div>
                  <div className="mt-0.5 text-zinc-100">{a.primaryPlatform}</div>
                </div>
                <div className="rounded-lg bg-white/[0.02] border border-white/5 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Followers</div>
                  <div className="mt-0.5 text-zinc-100">{formatFollowers(a.followers || 0)}</div>
                </div>
                <div className="rounded-lg bg-white/[0.02] border border-white/5 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Niches</div>
                  <div className="mt-0.5 text-zinc-100 truncate">{a.niches || "—"}</div>
                </div>
              </div>

              {a.about && (
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed line-clamp-3">{a.about}</p>
              )}

              <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-3 text-zinc-500">
                  <span>{a.email}</span>
                  {a.samples && (
                    <a
                      href={a.samples}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200"
                    >
                      Portfolio <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-white/10 bg-white/[0.02] text-zinc-200 hover:bg-white/5"
                    disabled={busyId === a.id}
                    onClick={() => setStatus(a, "Reviewing")}
                    data-testid={`app-review-${a.id}`}
                  >
                    Review
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white"
                    disabled={busyId === a.id}
                    onClick={() => setStatus(a, "Accepted")}
                    data-testid={`app-accept-${a.id}`}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" /> Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-white/10 bg-white/[0.02] text-zinc-200 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/30"
                    disabled={busyId === a.id}
                    onClick={() => setStatus(a, "Rejected")}
                    data-testid={`app-reject-${a.id}`}
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                  <button
                    onClick={() => remove(a)}
                    disabled={busyId === a.id}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                    aria-label="Delete"
                    data-testid={`app-delete-${a.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default Applications;
